import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Platform, Pressable, View, TextInputProps } from 'react-native';
import { Props } from 'shared/@types';
import { TextInput, TextInputRef } from 'shared/components/form/text-input';
import { Text } from 'shared/components/text';
import { runAfterInteractions } from 'shared/library/run-after-interactions';

export type CodeInputRef = { getValue: () => string };

const CodeInput = forwardRef<
  CodeInputRef,
  Props<{
    length: number;
    inputProps?: TextInputProps;
    onEdit?: () => void;
    onComplete: (value: string) => void;
    testID?: string;
  }>
>(({ length, inputProps, onEdit, onComplete, testID }, ref) => {
  const [value, setValue] = useState('');
  const inputRef = useRef<TextInputRef>(null);
  const shouldAutoFocus = inputProps?.autoFocus;

  function handleChange(text: string) {
    text = text.replace(/\D/g, '');

    onEdit?.();

    if (text.length <= length) {
      setValue(text);
      if (text.length === length) onComplete?.(text);
    }
  }

  useImperativeHandle(ref, () => ({
    getValue: () => value,
  }));

  useEffect(() => {
    if (!shouldAutoFocus) {
      return undefined;
    }

    return runAfterInteractions(() => {
      if (Platform.OS === 'web' && typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => inputRef.current?.focus());
        return;
      }

      inputRef.current?.focus();
    });
  }, [shouldAutoFocus]);

  return (
    <Pressable
      testID={testID}
      className={'h-12 flex-row items-center md:h-16'}
      onPress={() => inputRef.current?.focus()}
    >
      <TextInput
        {...inputProps}
        autoFocus={false}
        className={'absolute inset-0 opacity-0'}
        ref={inputRef}
        value={value}
        placeholder={''}
        onChangeText={(val: string) => handleChange(val)}
        onSubmitEditing={() => onComplete(value)}
        keyboardType="number-pad"
        textContentType="oneTimeCode"
        autoComplete="one-time-code"
        importantForAutofill="yes"
        maxLength={length}
      />
      <View className={'pointer-events-none h-full w-full flex-1 flex-row justify-center gap-3'}>
        {Array.from({ length }).map((_, i) => {
          const char = value[i] ?? '';
          const isActive = i === value.length;

          return (
            <View
              key={i}
              className="border-brand-brown-dark/15 h-full min-w-0 flex-1 items-center justify-center rounded-lg border bg-white md:rounded-xl"
            >
              <Text className="text-brand-brown-dark text-xl font-semibold md:text-2xl">
                {char}
              </Text>

              {isActive && <View className="absolute bottom-2 h-0.5 w-4 animate-pulse bg-black" />}
            </View>
          );
        })}
      </View>
    </Pressable>
  );
});

CodeInput.displayName = 'CodeInput';

export { CodeInput };
